<my-content placeholder="main">
    <div class="privacyContainer">

        <div class="title">Privacy Policy</div>

        <div>
            EasyLink Services respects your right to privacy and is committed to safeguarding the privacy of our
            customers and website visitors. We adhere to the Australian Privacy Principles contained in the Privacy Act
            1988 (Cth). This policy sets out how we collect and treat your personal information.
        </div>
        <div>
            Last updated: <strong>19 January 2024</strong>.
        </div>

        <hr />
        <div class="heading">1.0 DEFINITIONS</div>
        <hr />
        <div>
            For the purposes of this Privacy Policy:
        </div>
        <div>
            <strong>Account</strong> means a unique account created for You to access our Service or parts of our
            Service.
        </div>
        <div>
            <strong>Affiliate</strong> means an entity that controls, is controlled by or is under common control with a
            party, where “control" means ownership of 50% or more of the shares, equity interest or other securities
            entitled to vote for election of directors or other managing authority.
        </div>
        <div>
            <strong>Application</strong> means the software program provided by the Company downloaded by You on any
            electronic device, named EasyLink Services
        </div>
        <div>
            <strong>Company</strong> (referred to as either “the Company", “We", “Us" or “Our" in this Agreement) refers
            to EasyLink Services
        </div>
        <div>
            <strong>Cookies</strong> are small files that are placed on Your computer, mobile device or any other device
            by a website, containing the details of Your browsing history on that website among its many uses.
        </div>
        <div>
            <strong>Country</strong> refers to: Australia
        </div>
        <div>
            <strong>Device</strong> means any device that can access the Service such as a computer, a cell phone or a
            digital tablet.
        </div>
        <div>
            <strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.
        </div>
        <div>
            <strong>Service</strong> refers to the Application or the Website or both.
        </div>
        <div>
            <strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of the
            Company. It refers to third-party companies or individuals employed by the Company to facilitate the
            Service, to provide the Service on behalf of the Company, to perform services related to the Service or to
            assist the Company in analysing how the Service is used.
        </div>
        <div>
            <strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the
            Service or from the Service infrastructure itself (for example, the duration of a page visit).
        </div>
        <div>
            <strong>Website</strong> refers to EasyLink, accessible from
            <a href="https://easylink.johnslyng.com.au/">
                https://easylink.johnslyng.com.au/
            </a>
        </div>
        <div>
            <strong>You</strong> means the individual accessing or using the Service, or the company, or other legal
            entity on behalf of which such individual is accessing or using the Service, as applicable.
        </div>

        <!--  -->
        <hr />
        <div class="heading">2.0 COLLECTING AND USING YOUR PERSONAL DATA</div>
        <hr />
        <div class="subHeading">
            2.1: Types of Data Collected
        </div>
        <div class="subsectionTitle">Personal Data</div>
        <div>
            While using Our Service, we may ask You to provide Us with certain personally identifiable information that
            can be used to contact or identify You. Personally identifiable information may include, but is not limited
            to:
            <ul>
                <li>Email address</li>
                <li>First name and last name</li>
                <li>Phone number</li>
                <li>Address, State, City</li>
            </ul>
        </div>

        <div class="subsectionTitle">Usage Data</div>
        <div>
            <div>Usage Data is collected automatically when using the Service.</div>
            <div>
                Usage Data may include information such as Your Device’s Internet Protocol address (e.g. IP address),
                browser type, browser version, the pages of our Service that You visit, the time and date of Your visit,
                the time spent on those pages, unique device identifiers and other diagnostic data.
            </div>
            <div>
                When You access the Service by or through a mobile device, We may collect certain information
                automatically, including, but not limited to, the type of mobile device You use, Your mobile device
                unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile
                Internet browser You use, unique device identifiers and other diagnostic data.
            </div>
            <div>
                We may also collect information that Your browser sends whenever You visit our Service or when You
                access the Service by or through a mobile device.
            </div>
        </div>

        <div class="subsectionTitle">Information Collected While Using the Application</div>
        <div>
            <div>
                While using Our Application, in order to provide features of Our Application, We may collect, with Your
                prior permission:
            </div>
            <ul>
                <li>Information regarding your location</li>
            </ul>
            <div>
                We use this information to provide features of Our Service, to improve and customize Our Service. The
                information may be uploaded to the Company's servers and/or a Service Provider's server or it may be
                simply stored on Your device.

            </div>
            <div>
                You can enable or disable access to this information at any time, through Your Device settings.
            </div>
        </div>

        <div class="subsectionTitle">Tracking Technologies and Cookies</div>
        <div>
            <div>
                We use Cookies and similar tracking technologies to track the activity on Our Service and store certain
                information. Tracking technologies used are beacons, tags, and scripts to collect and track information
                and to improve and analyse Our Service. The technologies We use may include:

            </div>
            <div>
                <ul>
                    <li>
                        Cookies or Browser Cookies. A cookie is a small file placed on Your Device. You can instruct
                        Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You
                        do not accept Cookies, You may not be able to use some parts of our Service. Unless you have
                        adjusted Your browser setting so that it will refuse Cookies, our Service may use Cookies.
                    </li>
                    <li>
                        Flash Cookies. Certain features of our Service may use local stored objects (or Flash Cookies)
                        to collect and store information about Your preferences or Your activity on our Service. Flash
                        Cookies are not managed by the same browser settings as those used for Browser Cookies. For more
                        information on how You can delete Flash Cookies, please read “Where can I change the settings
                        for disabling, or deleting local shared objects?" available
                        <a
                            href="https://helpx.adobe.com/flash-player/kb/disable-local-shared-objects-flash.html#main_Where_can_I_change_the_settings_for_disabling__or_deleting_local_shared_objects_">here
                        </a>.

                    </li>
                    <li>
                        Web Beacons. Certain sections of our Service and our emails may contain small electronic files
                        known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that
                        permit the Company, for example, to count users who have visited those pages or opened an email
                        and for other related website statistics (for example, recording the popularity of a certain
                        section and verifying system and server integrity).
                    </li>
                </ul>
                <div>
                    Cookies can be “Persistent" or “Session" Cookies. Persistent Cookies remain on Your personal
                    computer or mobile device when You go offline, while Session Cookies are deleted as soon as You
                    close Your web browser.
                </div>
            </div>
        </div>

        <div class="subHeading">2.2: Use of Your Personal Data</div>
        <div>
            <div>
                The Company may use Personal Data for the following purposes:
            </div>
            <div>
                <ul>
                    <li>
                        To provide and maintain our Service, including to monitor the usage of our Service.
                    </li>
                    <li>
                        To manage Your Account: to manage Your registration as a user of the Service. The Personal Data
                        You provide can give You access to different functionalities of the Service that are available
                        to You as a registered user.
                    </li>
                    <li>
                        For the performance of a contract: the development, compliance, and undertaking of the purchase
                        contract for the products, items, or services You have purchased or of any other contract with
                        Us through the Service.
                    </li>
                    <li>
                        To contact You: by email, telephone calls, SMS, or other equivalent forms of electronic
                        communication, such as a mobile application’s push notifications regarding updates or
                        informative communications related to the functionalities, products, or contracted services,
                        including the security updates, when necessary or reasonable for their implementation.
                    </li>
                    <li>
                        To provide You with news, special offers, and general information about other goods, services,
                        and events which we offer that are similar to those that you have already purchased or enquired
                        about unless You have opted not to receive such information.
                    </li>
                    <li>
                        To manage Your requests: To attend and manage Your requests to Us.
                    </li>
                    <li>
                        For business transfers: We may use Your information to evaluate or conduct a merger,
                        divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or
                        all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar
                        proceeding, in which Personal Data held by Us about our Service users is among the assets
                        transferred.
                    </li>
                    <li>
                        For other purposes: We may use Your information for other purposes, such as data analysis,
                        identifying usage trends, determining the effectiveness of our promotional campaigns, and to
                        evaluate and improve our Service, products, services, marketing, and your experience.
                    </li>
                </ul>
            </div>
            <div>
                We may share Your personal information in the following situations:
            </div>
            <ul>
                <li>
                    With Service Providers: We may share Your personal information with Service Providers to monitor
                    and analyse the use of our Service, for payment processing, to contact You.
                </li>
                <li>
                    For business transfers: We may share or transfer Your personal information in connection with,
                    or during negotiations of, any merger, sale of company assets, financing, or acquisition of all
                    or a portion of Our business to another company.
                </li>
                <li>
                    With Affiliates: We may share Your information with Our affiliates, in which case we will
                    require those affiliates to honour this Privacy Policy. Affiliates include Our parent company
                    and any other subsidiaries, joint venture partners or other companies that We control or that
                    are under common control with Us.
                </li>
                <li>
                    With business partners: We may share Your information with Our business partners to offer You
                    certain products, services or promotions.
                </li>
                <li>
                    With other users: when You share personal information or otherwise interact in the public areas
                    with other users, such information may be viewed by all users and may be publicly distributed
                    outside.
                </li>
                <li>
                    With Your Consent: We may disclose Your personal information for any other purpose with Your
                    consent.
                </li>
            </ul>
        </div>

        <div class="subHeading">2.3: Retention of Your Personal Data</div>
        <div>
            <div>
                The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in
                this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with
                our legal obligations (for example, if we are required to retain your data to comply with applicable
                laws), resolve disputes, and enforce our legal agreements and policies.
            </div>
            <div>
                The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally retained
                for a shorter period of time, except when this data is used to strengthen the security or to improve the
                functionality of Our Service, or We are legally obligated to retain this data for longer time periods.
            </div>
        </div>

        <div class="subHeading">2.4: Transfer of Your Personal Data</div>
        <div>
            <div>
                Your information, including Personal Data, is processed at the Company’s operating offices and in any
                other places where the parties involved in the processing are located. It means that this information
                may be transferred to — and maintained on — computers located outside of Your state, country or other
                governmental jurisdiction where the data protection laws may differ than those from Your jurisdiction.
            </div>
            <div>
                Your consent to this Privacy Policy followed by Your submission of such information represents Your
                agreement to that transfer.
            </div>
            <div>
                The Company will take all steps reasonably necessary to ensure that Your data is treated securely and in
                accordance with this Privacy Policy and no transfer of Your Personal Data will take place to an
                organization or a country unless there are adequate controls in place including the security of Your
                data and other personal information.
            </div>
        </div>

        <div class="subHeading">2.5: Disclosure of Your Personal Data</div>
        <div>
            <div class="subsectionTitle">Business Transactions</div>
            <div>
                If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be
                transferred. We will provide notice before Your Personal Data is transferred and becomes subject to a
                different Privacy Policy.

            </div>
            <div class="subsectionTitle">Law enforcement</div>
            <div>
                Under certain circumstances, the Company may be required to disclose Your Personal Data if required to
                do so by law or in response to valid requests by public authorities (e.g. a court or a government
                agency).

            </div>
            <div class="subsectionTitle">Other legal requirements</div>
            <div>
                The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:
                <ul>
                    <li>
                        Comply with a legal obligation
                    </li>
                    <li>
                        Protect and defend the rights or property of the Company
                    </li>
                    <li>
                        Prevent or investigate possible wrongdoing in connection with the Service
                    </li>
                    <li>
                        Protect the personal safety of Users of the Service or the public
                    </li>
                    <li>
                        Protect against legal liability
                    </li>
                </ul>
            </div>
        </div>


        <div class="subHeading">2.6: Security of Your Personal Data</div>
        <div>
            The security of Your Personal Data is important to Us, but remember that no method of transmission over the
            Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable
            means to protect Your Personal Data, We cannot guarantee its absolute security.
        </div>

        <!--  -->
        <hr />
        <div class="heading">3.0 DETAILED INFORMATION ON THE PROCESSING OF YOUR PERSONAL DATA</div>
        <hr />

        <div class="subHeading">3.1: Payments</div>
        <div>
            <div>

                We may provide paid products and/or services within the Service. In that case, we may use third-party
                services for payment processing (e.g. payment processors).
            </div>
            <div>
                These payment processors adhere to the standards set by PCI-DSS as managed by the PCI Security Standards
                Council, which is a joint effort of brands like Visa, Mastercard, American Express and Discover. PCI-DSS
                requirements help ensure the secure handling of payment information.
            </div>
            <div class="subsectionTitle">Windcave</div>
            <div>
                Their Privacy Policy can be viewed at
                <a href="https://sec.windcave.com/pxmi3/privacy-policy">
                    https://sec.windcave.com/pxmi3/privacy-policy
                </a>
            </div>
        </div>

        <!--  -->
        <hr />
        <div class="heading">4.0 LINKS TO OTHER WEBSITES</div>
        <hr />
        <div>
            <div>
                Our Service may contain links to other websites that are not operated by Us. If You click on a third
                party link, You will be directed to that third party’s site. We strongly advise You to review the
                Privacy Policy of every site You visit.
            </div>
            <div>
                We have no control over and assume no responsibility for the content, privacy policies or practices of
                any third party sites or services.
            </div>
        </div>

        <!--  -->
        <hr />
        <div class="heading">5.0 CHANGES TO THIS PRIVACY POLICY</div>
        <hr />
        <div>
            <div>
                We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new
                Privacy Policy on this page.
            </div>
            <div>
                We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming
                effective and update the “Last updated" date at the top of this Privacy Policy.
            </div>
            <div>
                You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy
                Policy are effective when they are posted on this page.
            </div>
        </div>

        <!--  -->
        <hr />
        <div class="heading">6.0 CONTACT US</div>
        <hr />
        <div>
            <div>
                If you have any questions about this Privacy Policy, You can contact us:
            </div>
            <ul>
                <li>
                    By email:
                    <a href="mailto:support@easylinkservices.com.au">
                        support@easylinkservices.com.au
                    </a>

                </li>
            </ul>
        </div>

    </div>
</my-content>